import { useContext, useState, useEffect } from "react";
import { LocaleContext } from "../../contexts/LocaleContext";
import Pagination from "../../components/Pagination";
import { Link } from "react-router-dom";
import DeleteConfirmation from "../../components/DeleteConfirmation";
import { customerService } from "../../services/customerService";
import Loading from "../../components/common/Loading";
import InLinneLoading from "../../components/common/InlineLoading";
import { toast } from "../../services/toastService";
import { params } from "../../config/params";
import { searchParams } from "../../config/searchParams";

const Customers = () => {
  const { t, lang } = useContext(LocaleContext);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    setLoading(true);
    getAllAsync();
  }, []);

  const getAllAsync = async () => {
    try {
      const result = await customerService.getAllAsync(params);
      setData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getAllSearchAsync = async (signal) => {
    try {
      const result = await customerService.getAllSearchAsync(searchParams, signal);
      setData(result);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (search.trim().length < 2) {
      params.pageNumber = 1;
      getAllAsync();
      return;
    }

    const controller = new AbortController();
    const delay = setTimeout(() => {
      searchParams.search = search.trim();
      searchParams.pageNumber = 1;
      getAllSearchAsync(controller.signal);
    }, 400);

    return () => {
      controller.abort();
      clearTimeout(delay);
    };
  }, [search]);

  const onPageChange = async (pageNumber) => {
    setLoading(true);
    if (!search) {
      params.pageNumber = pageNumber;
      getAllAsync();
      return;
    }
    searchParams.pageNumber = pageNumber;
    await getAllSearchAsync();
  };

  const handleChecked = async (id) => {
    setLoadingId(id);
    const previousData = data;

    try {
      const safeItems = Array.isArray(data?.items) ? data.items : [];
      const updatedItems = safeItems.map((item) =>
        item.id === id ? { ...item, isActive: !item.isActive } : item,
      );
      setData({ ...data, items: updatedItems });

      const response = await customerService.toggleActivate(id);

      if (!response.isSuccess) {
        setData(previousData);
        response.key
          ? toast.error(t(response.key))
          : toast.error(t("anErrorOccurredPleaseTryAgainLater"));
        console.log(response);
        return;
      }

      toast.success(t("recordUpdatedSuccessfully"));
    } catch (error) {
      setData(previousData);
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      console.log(error);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <>
      {loading && <Loading />}
      <section className="Customers">
        <div className="default-heading">{t("Customers")}</div>
        <section className="section-dashboard-cards mb-3 d-none">
          <div className="row">
            <div className="col-12 col-md-3">
              <div className="d-card d-card-primary">
                <div className="d-card-header">{t("totalUsers")}</div>
                <div className="d-card-body">
                  <div className="d-card-body-1">
                    <span>7,125.70</span>
                    <span>{t("newThisMonth")}: 3</span>
                  </div>
                  <div className="d-card-body-2">
                    <span>52.0%</span>
                    <i className="fas fa-arrow-circle-up"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-3">
              <div className="d-card d-card-dark">
                <div className="d-card-header">{t("onlineUsers")}</div>
                <div className="d-card-body">
                  <div className="d-card-body-1">
                    <span>7,125.70</span>
                    <span>{t("newThisMonth")}: 3</span>
                  </div>
                  <div className="d-card-body-2">
                    <span>52.0%</span>
                    <i className="fas fa-arrow-circle-up"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-3">
              <div className="d-card d-card-primary">
                <div className="d-card-header">{t("Customer")}</div>
                <div className="d-card-body">
                  <div className="d-card-body-1">
                    <span>7,125.70</span>
                    <span>{t("newThisMonth")}: 3</span>
                  </div>
                  <div className="d-card-body-2">
                    <span>52.0%</span>
                    <i className="fas fa-arrow-circle-up"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-3">
              <div className="d-card d-card-dark">
                <div className="d-card-header">{t("totalRevenue")}</div>
                <div className="d-card-body">
                  <div className="d-card-body-1">
                    <span>7,125.70</span>
                    <span>{t("newThisMonth")}: 3</span>
                  </div>
                  <div className="d-card-body-2">
                    <span>52.0%</span>
                    <i className="fas fa-arrow-circle-up"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="section-grid">
          <div className="grid-card">
            <div className="grid-card-header">
              <strong>{t("customerManagement")}</strong>
              <p>{t("viewAndManageAllCustomersInTheSystem")}</p>
            </div>
            <div className="grid-card-body">
              <div className="table-header">
                <div className="table-header-icons hidden">
                  <div className="icon-box">
                    <i className="fas fa-file-pdf"></i>
                  </div>
                  <div className="icon-box">
                    <i className="fas fa-file-excel"></i>
                  </div>
                  <div className="icon-box">
                    <i className="fas fa-print"></i>
                  </div>
                </div>
                <div className="table-search">
                  <div className="input-icon input-icon-width">
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="form-control input-search"
                      placeholder={t("search")}
                    />
                    <i className="fas fa-search"></i>
                  </div>
                  <div className="grid-btn">
                    <Link to="/customer-form">
                      <button className="btn-default my-bg-secondary">
                        <i className="fas fa-plus"></i> {t("addNewUser")}
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
              <div className="table-delete-option mt-3">
                <b>{t("allCustomers")}</b>
                <div className="dropdown hidden">
                  <i
                    className="fas fa-filter cursor-pointer"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  />
                  <span></span>
                  <ul className="dropdown-menu">
                    <li>
                      <a className="dropdown-item" href="#">
                        <input type="radio" /> {t("status")}
                      </a>
                    </li>
                    <li>
                      <a className="dropdown-item" href="#">
                        <input type="radio" /> {t("mobileNo")}
                      </a>
                    </li>
                    <li>
                      <a className="dropdown-item" href="#">
                        <input type="radio" /> {t("city")}
                      </a>
                    </li>
                    <li>
                      <a className="dropdown-item" href="#">
                        <input type="radio" /> {t("date")}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="my-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th>
                        <input type="checkbox" />
                      </th>
                      <th>{t("userName")}</th>
                      <th>{t("mobileNo")}</th>
                      <th>{t("email")}</th>
                      <th>{t("noOfBookings")}</th>
                      <th>{t("city")}</th>
                      <th>{t("status")}</th>
                      <th>{t("date")}</th>
                      <th>{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data &&
                      data.items &&
                      data.items.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <input type="checkbox" />
                          </td>
                          <td>{item.fullName}</td>
                          <td>{item.phoneNumberCode}{item.phoneNumber}</td>
                          <td>{item.email}</td>
                          <td>{item.noOfBookings}</td>
                          <td>{item.city?.[lang]}</td>
                          <td>
                            {loadingId === item.id ? (
                              <InLinneLoading color={"text-primary"} />
                            ) : (
                              <div className="form-check form-switch flex-center">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`switch-${item.id}`}
                                  checked={item.isActive}
                                  onChange={() => handleChecked(item.id)}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`switch-${item.id}`}
                                ></label>
                              </div>
                            )}
                          </td>
                          <td>{item.dateJoined?.toDate()}</td>
                          <td>
                            <i
                              className="fas fa-ellipsis-vertical"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            ></i>
                            <ul className="dropdown-menu">
                              <li>
                                <a className="dropdown-item" href={`/customer-form/${item.id}`}>
                                  <i className="fas fa-edit"></i> {t("edit")}
                                </a>
                              </li>
                              <li>
                                <Link
                                  className="dropdown-item"
                                  to={`/customer-details/${item.id}`}
                                >
                                  <i className="fas fa-eye"></i> {t("view")}
                                </Link>
                              </li>
                              {/* <li>
                                <a
                                  className="dropdown-item"
                                  href="#"
                                  onClick={(e) => e.preventDefault()}
                                  data-bs-toggle="modal"
                                  data-bs-target="#my-modal"
                                >
                                  <i className="fas fa-trash-alt text-danger"></i>{" "}
                                  {t("delete")}
                                </a>
                              </li> */}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    {data?.items?.length === 0 && (
                      <tr>
                        <td colSpan="100%" className="text-center py-3">
                          {t("noResultsFound")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="grid-footer">
              {data && (
                <Pagination
                  t={t}
                  pageNumber={data.pageNumber}
                  totalPages={data.totalPages}
                  hasNextPage={data.hasNextPage}
                  hasPreviousPage={data.hasPreviousPage}
                  onPageChange={onPageChange}
                />
              )}
            </div>
          </div>
        </section>
      </section>
      <DeleteConfirmation t={t} />
    </>
  );
};

export default Customers;
